import { deriveCategory } from "@/v2-backoffice-client/translation/translation.utils"
import {
  ENGLISH_LANGUAGE_CODE,
  LANGUAGE_DISPLAY_ORDER,
  NORWEGIAN_BOKMAL_LANGUAGE_CODE,
} from "@/v2-backoffice-client/translation/translation.constants"
import type {
  SupportedLanguage,
  TranslationBoard,
  TranslationCategory,
  TranslationRow,
  TypeValueAttributes,
} from "@/v2-backoffice-client/translation/translation.types"
import type { TranslationCatalogQuery } from "@/v2-backoffice-client/translation/catalog/__types__/translation.catalog.query"

// Fallback if the languages query returns nothing so the table columns +
// missing count still work; kept in LANGUAGE_DISPLAY_ORDER order.
const FALLBACK_LANGUAGES: SupportedLanguage[] = [
  { code: "en-GB", label: "English" },
  { code: "nb-NO", label: "Norsk" },
  { code: "nn-NO", label: "Nynorsk" },
  { code: "sv-SE", label: "Svenska" },
  { code: "da-DK", label: "Dansk" },
]

function byDisplayOrder(a: SupportedLanguage, b: SupportedLanguage): number {
  const rank = (code: string) => {
    const i = LANGUAGE_DISPLAY_ORDER.indexOf(code)
    return i === -1 ? LANGUAGE_DISPLAY_ORDER.length : i
  }
  return rank(a.code) - rank(b.code) || a.label.localeCompare(b.label)
}

// Sidebar bucket for a feature code. UI codes follow ADR-0025's
// UI_<Feature>_<Surface>_<Slot> shape, so the first two segments name the
// feature ("UI_Dataset"); entity/DB-label codes bucket on their entity
// ("Property"). Measured on the current catalog this yields ~30 buckets —
// small enough for a sidebar, large enough to be meaningful.
export function deriveCategoryKey(code: string): string {
  const parts = code.split("_")
  if (parts[0] === "UI" && parts.length > 2) return `${parts[0]}_${parts[1]}`
  return parts[0]
}

function categoryLabel(key: string): string {
  return key.startsWith("UI_") ? key.slice(3) : key
}

// Shared getTranslationLanguages -> editor language mapping, so every host
// of TranslationEditor (the Translation tab, the layer sheet's Details tab)
// renders the exact same field set in the exact same order.
export function toSupportedLanguages(
  languages: Array<{
    Code: string
    Name?: string | null
    NativeName?: string | null
    IsSupported: boolean
  }>,
): SupportedLanguage[] {
  const supported = languages
    .filter((lang) => lang.IsSupported)
    .map((lang) => ({
      code: lang.Code,
      label: lang.Name ?? lang.NativeName ?? lang.Code,
    }))
    .sort(byDisplayOrder)
  return supported.length > 0 ? supported : FALLBACK_LANGUAGES
}

function selectLanguages(data?: TranslationCatalogQuery): SupportedLanguage[] {
  return toSupportedLanguages(data?.getTranslationLanguages ?? [])
}

/**
 * The single seam between the Translation tab and the catalog query: one
 * pass over the query result producing everything every surface renders —
 * rows, sidebar categories with stats, and the incomplete set. Centralising
 * the pass keeps the hot path (a few thousand rows, re-run per catalog
 * refetch) from being repeated per consumer.
 */
export function buildTranslationBoard(
  data?: TranslationCatalogQuery,
): TranslationBoard {
  const languages = selectLanguages(data)
  const features = data?.getFeatureTranslations ?? []
  const typeValues = data?.getTypeValueTranslations ?? []

  const rows: TranslationRow[] = []
  const rowsByCode = new Map<string, TranslationRow>()
  const categoryByKey = new Map<string, TranslationCategory>()
  const incompleteRows: TranslationRow[] = []
  const deletedRows: TranslationRow[] = []

  // Soft-deleted rows skip every aggregate (sidebar badges, Missing
  // translations, duplicates, add-objects, rowsByCode's exists-check) so
  // none of those surfaces have to know deleted rows exist. They're kept in
  // `deletedRows` instead, purely so the catalog's Status filter can list
  // and restore them — a deleted row invisible in the Backoffice used to be
  // unfixable there (see the 2026-07 sg-events publish incident).
  function addRow(row: TranslationRow, categoryKey: string) {
    if (row.isDeleted) {
      deletedRows.push(row)
      return
    }

    rows.push(row)
    rowsByCode.set(row.code, row)
    if (row.missingCount > 0) incompleteRows.push(row)

    let category = categoryByKey.get(categoryKey)
    if (!category) {
      category = {
        key: categoryKey,
        label: categoryLabel(categoryKey),
        section: categoryKey.startsWith("UI_") ? "ui" : "data",
        rows: [],
        total: 0,
        incomplete: 0,
      }
      categoryByKey.set(categoryKey, category)
    }
    category.rows.push(row)
    category.total++
    if (row.missingCount > 0) category.incomplete++
  }

  // The JSONObject scalar arrives as `any`. A type table's attributes are
  // always a JSON object; anything else in the column (an array, a bare
  // scalar) is data the seed cannot read either, so it is surfaced as "none"
  // rather than handed to an editor that would present it as an object.
  function toAttributes(raw: unknown): TypeValueAttributes | null {
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
      return null
    }
    return raw as TypeValueAttributes
  }

  function toTexts(
    raw: {
      LanguageCode: string
      Name?: string | null
      Description?: string | null
    }[],
  ) {
    const texts = raw.map((text) => ({
      languageCode: text.LanguageCode,
      name: text.Name ?? "",
      description: text.Description ?? "",
    }))
    const nameOf = (code: string) =>
      texts.find((text) => text.languageCode === code)?.name ?? ""
    return { texts, nameOf }
  }

  for (const feature of features) {
    const { texts, nameOf } = toTexts(feature.Texts)
    const missingCount = languages.filter((lang) => !nameOf(lang.code)).length

    addRow(
      {
        code: feature.Code,
        shortCode: feature.Code,
        objectType: "Feature",
        attributes: null,
        category: deriveCategory(feature.Code),
        categoryKey: deriveCategoryKey(feature.Code),
        linkGroupCode: feature.LinkGroupCode ?? null,
        isLinkMaster: feature.IsLinkMaster ?? false,
        texts,
        nbName: nameOf(NORWEGIAN_BOKMAL_LANGUAGE_CODE),
        enName: nameOf(ENGLISH_LANGUAGE_CODE),
        missingCount,
        isDeleted: feature.IsDeleted,
      },
      deriveCategoryKey(feature.Code),
    )
  }

  // Type values (data labels / enum values) join the same board: their
  // ObjectKey is the row code (unique across tables and what the upsert
  // needs), and the owning table is the category — so the 133 nameless
  // industry.IndustrialClassifications kind of gap surfaces in the Missing
  // translations queue like any UI text.
  for (const typeValue of typeValues) {
    const { texts, nameOf } = toTexts(typeValue.Texts)
    const missingCount = languages.filter((lang) => !nameOf(lang.code)).length

    addRow(
      {
        code: typeValue.ObjectKey,
        shortCode: typeValue.Code,
        objectType: "TypeValue",
        attributes: toAttributes(typeValue.Attributes),
        category: typeValue.TableName,
        categoryKey: typeValue.TableName,
        linkGroupCode: null,
        isLinkMaster: false,
        texts,
        nbName: nameOf(NORWEGIAN_BOKMAL_LANGUAGE_CODE),
        enName: nameOf(ENGLISH_LANGUAGE_CODE),
        missingCount,
        isDeleted: typeValue.IsDeleted,
      },
      typeValue.TableName,
    )
  }

  // UI categories first (the tab's main audience), then data labels;
  // alphabetical within a section.
  const sectionRank = (section: TranslationCategory["section"]) =>
    section === "ui" ? 0 : 1
  const categories = [...categoryByKey.values()].sort(
    (a, b) =>
      sectionRank(a.section) - sectionRank(b.section) ||
      a.label.localeCompare(b.label),
  )

  return {
    languages,
    rows,
    rowsByCode,
    categories,
    categoryByKey,
    incompleteRows,
    deletedRows,
  }
}
